/**
 * Inline `$...$` math, restricted with the pandoc rule so currency is not
 * mistaken for math: the opening `$` needs a non-space right after it, the
 * closing `$` a non-space right before it and no digit right after it.
 * "$3.00 plus $2.50" therefore stays text ("plus " ends in a space), while
 * "$x + 1$" is still math. The question bank authors currency with bare `$`
 * and never uses single-dollar math, so this only has to be safe, not clever.
 */
export const INLINE_DOLLAR_SOURCE = String.raw`\$(?![\s$])(?:[^$\n\\]|\\.)+?(?<![\s\\])\$(?!\d)`;

export interface TableBlock {
  rows: string[][];
  /** true when the source had a `---|---` separator row */
  explicitHeader: boolean;
}

const SEPARATOR_CELL = /^:?-{2,}:?$/;

function splitRow(line: string): string[] {
  let t = line.trim();
  // "| a | b |" is a bordered row; "| a | b" (bank two-way tables) has an
  // intentionally empty top-left corner cell, so only strip a leading pipe
  // when the row is closed by a trailing one.
  if (t.startsWith('|') && t.endsWith('|') && t.length > 1) t = t.slice(1, -1);
  else if (t.endsWith('|')) t = t.slice(0, -1);
  return t.split('|').map((c) => c.trim());
}

/**
 * Splits plain text into text chunks and pipe tables. A table is 2+ consecutive
 * lines that all have the same number of pipe-separated cells (>= 2). Bank
 * tables usually have no `---` separator row; the first row is the header.
 * Single lines such as "|2x - 5| < 9" or "P(A|B)" are never tables.
 */
export function splitTables(input: string): Array<{ text: string } | { table: TableBlock }> {
  const lines = input.split('\n');
  const out: Array<{ text: string } | { table: TableBlock }> = [];
  let buf: string[] = [];
  const flush = () => {
    if (buf.length) out.push({ text: buf.join('\n') });
    buf = [];
  };

  let i = 0;
  while (i < lines.length) {
    const cells = lines[i].includes('|') ? splitRow(lines[i]) : null;
    if (cells && cells.length >= 2) {
      let j = i + 1;
      const rows = [cells];
      while (j < lines.length && lines[j].includes('|')) {
        const next = splitRow(lines[j]);
        if (next.length !== cells.length) break;
        rows.push(next);
        j++;
      }
      if (rows.length >= 2) {
        const isSep = (r: string[]) => r.every((c) => SEPARATOR_CELL.test(c));
        const explicitHeader = isSep(rows[1]);
        const body = rows.filter((r) => !isSep(r));
        if (body.length >= 2 || (explicitHeader && body.length >= 1)) {
          flush();
          out.push({ table: { rows: body, explicitHeader } });
          i = j;
          continue;
        }
      }
    }
    buf.push(lines[i]);
    i++;
  }
  flush();
  return out;
}
